import { useEffect } from 'react';
import { initializeSdk, isStatsigInitialized } from '@eventbrite/legacy-infra.statsig';

import { getOptions, getUser, shouldInitStatsig } from './utils';
import type { StatsigListingsConfig } from './types';

const initCallbacks: Array<() => void> = [];
let initPromise: Promise<void> | null = null;

const STATSIG_READY_POLL_MS = 50;
const STATSIG_READY_TIMEOUT_MS = 10_000;

const waitForStatsigJsReady = async (): Promise<void> => {
  const deadline = Date.now() + STATSIG_READY_TIMEOUT_MS;
  while (Date.now() < deadline) {
    if (isStatsigInitialized()) {
      return;
    }
    await new Promise((r) => setTimeout(r, STATSIG_READY_POLL_MS));
  }

  if (!isStatsigInitialized()) {
    throw new Error('Statsig JS client did not report ready after initializeSdk');
  }
};

const flushPendingStatsigCallbacks = (): void => {
  while (initCallbacks.length > 0) {
    const callback = initCallbacks.shift();
    callback?.();
  }
};

const initializeListingsStatsig = async ({
  clientKey,
  stableId,
  custom,
  environmentTier,
  deactivateSdk,
}: StatsigListingsConfig): Promise<void> => {
  const statsigUser = getUser(stableId, custom);
  const statsigOptions = getOptions(stableId, environmentTier);

  await initializeSdk({
    clientKey,
    user: statsigUser,
    options: statsigOptions,
    deactivateSdk,
  });

  await waitForStatsigJsReady();
  flushPendingStatsigCallbacks();
};

/**
 * Reset module state - FOR TESTING ONLY
 * @internal
 */
export const __resetForTesting = (): void => {
  initCallbacks.length = 0;
  initPromise = null;
};

/**
 * Register a callback to run when Statsig is initialized.
 * If already initialized, runs immediately.
 */
export const onStatsigReady = (callback: () => void): void => {
  if (isStatsigInitialized()) {
    callback();
  } else {
    initCallbacks.push(callback);
  }
};

/**
 * Initialize Statsig client
 */
export const useInitStatsig = (config: StatsigListingsConfig): void => {
  const { clientKey, stableId, custom, environmentTier, deactivateSdk } = config;

  useEffect(() => {
    if (!shouldInitStatsig(config) || isStatsigInitialized() || initPromise) {
      return;
    }

    initPromise = initializeListingsStatsig(config).catch((error) => {
      initPromise = null;

      if (error instanceof Error && error.message === 'Statsig JS client did not report ready after initializeSdk') {
        console.error('[Statsig Error] Statsig JS client did not report ready after initializeSdk', {
          stableId: stableId || 'missing',
          environmentTier: environmentTier || 'missing',
        });
      } else {
        console.error('[Statsig Error] Statsig initialization failed', {
          error,
          stableId: stableId || 'missing',
          environmentTier: environmentTier || 'missing',
        });
      }
    });
  }, [clientKey, stableId, custom, environmentTier, deactivateSdk]);
};
